import React, { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment-timezone';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

  const SimpleCalendar = ({ appointments = [], onDateClick, onAppointmentClick, currentDate, setCurrentDate, photographerFilters, getPhotographerColor, calendarView, setCalendarView, pendingSubmissions = [], dayDriveTimes = {}, loadingDriveTimes = false, calendarEvents = [], scheduleBlocks = [], teamMembers = [], accountSettings = {}, products = [], setSelectedEvent, setShowEventDetails }) => {
    // State for date picker popover
    const [showDatePicker, setShowDatePicker] = useState(false);
    const datePickerRef = React.useRef(null);
    
    // Close date picker when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
          setShowDatePicker(false);
        }
      };
      
      if (showDatePicker) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showDatePicker]);
    
    // Combine appointments, Google Calendar events, blocks, and pending submissions filtered by photographer
    const allEvents = useMemo(() => {
      const localAppointments = (appointments || [])
        .filter(apt => {
          // If no filters set yet, show all
          if (Object.keys(photographerFilters || {}).length === 0) return true;
          // Filter by photographer if filter exists
          return photographerFilters[apt.photographer_id] !== false;
        })
        .map(apt => ({
          ...apt,
          source: 'local',
          title: apt.service_type || 'Appointment',
          start: apt.scheduled_date,
          type: 'appointment',
          color: getPhotographerColor ? getPhotographerColor(apt.photographer_id) : null
        }));
      
      const googleEvents = (calendarEvents || [])
        .filter(event => {
          // If no filters set yet, show all
          if (Object.keys(photographerFilters || {}).length === 0) return true;
          // Filter by team member if filter exists
          return photographerFilters[event.team_member_id] !== false;
        })
        .map(event => ({
          ...event,
          type: 'google_event',
          color: getPhotographerColor ? getPhotographerColor(event.team_member_id) : null
        }));
      
      // Add schedule blocks as events
      const blockEvents = (scheduleBlocks || []).flatMap(block => {
        // Default gray color for unassigned blocks
        const defaultBlockColor = { bg: 'bg-gray-200', text: 'text-gray-700', hover: 'hover:bg-gray-300', dot: 'bg-gray-500', hex: '#6b7280' };
        
        // If no photographers selected, show as a general block
        if (!block.photographer_ids || block.photographer_ids.length === 0) {
          return [{
            ...block,
            photographer_id: null,
            type: 'block',
            title: block.title || 'Blocked',
            start: block.start_date,
            scheduled_date: block.start_date,
            color: defaultBlockColor
          }];
        }
        // For each photographer in the block, create an event
        return block.photographer_ids.map(photographerId => {
          const photographer = teamMembers.find(m => m.id === photographerId);
          return {
            ...block,
            photographer_id: photographerId,
            photographer_name: photographer?.name || '',
            type: 'block',
            title: block.title || 'Blocked',
            start: block.start_date,
            scheduled_date: block.start_date,
            color: getPhotographerColor ? getPhotographerColor(photographerId) : defaultBlockColor
          };
        });
      }).filter(evt => {
        if (!evt.photographer_id) return true; // Always show unassigned blocks
        if (Object.keys(photographerFilters || {}).length === 0) return true;
        return photographerFilters[evt.photographer_id] !== false;
      });
      
      // Add pending booking requests as events (styled differently)
      const pendingEvents = (pendingSubmissions || [])
        .filter(sub => sub.scheduled_date && sub.scheduled_time) // Only include submissions with scheduling info
        .filter(sub => {
          if (Object.keys(photographerFilters || {}).length === 0) return true;
          if (!sub.photographer_id) return true; // Show "any photographer" submissions
          return photographerFilters[sub.photographer_id] !== false;
        })
        .map(sub => {
          // Get photographer's color, fallback to gray for "any photographer"
          const photographerColor = sub.photographer_id && getPhotographerColor 
            ? getPhotographerColor(sub.photographer_id) 
            : { bg: 'bg-gray-200', text: 'text-gray-700', hover: 'hover:bg-gray-300', dot: 'bg-gray-500', hex: '#6b7280' };
          
          return {
            ...sub,
            type: 'pending',
            title: `⏳ ${sub.client_name || 'Pending Request'}`,
            start: sub.scheduled_date,
            scheduled_date: sub.scheduled_date,
            scheduled_time: sub.scheduled_time,
            duration_minutes: sub.duration || 60,
            photographer_id: sub.photographer_id,
            color: photographerColor,
            isPending: true // Flag for striped/dashed styling
          };
        });
      
      return [...localAppointments, ...googleEvents, ...blockEvents, ...pendingEvents];
    }, [appointments, calendarEvents, scheduleBlocks, photographerFilters, getPhotographerColor, teamMembers, pendingSubmissions]);

    // Get the current month's calendar data
    const getCalendarData = () => {
      const startOfMonth = moment(currentDate).startOf('month');
      const endOfMonth = moment(currentDate).endOf('month');
      const startOfCalendar = moment(startOfMonth).startOf('week');
      const endOfCalendar = moment(endOfMonth).endOf('week');

      const days = [];
      let current = moment(startOfCalendar);

      while (current.isBefore(endOfCalendar) || current.isSame(endOfCalendar, 'day')) {
        days.push({
          date: current.clone(),
          isCurrentMonth: current.isSame(currentDate, 'month'),
          isToday: current.isSame(moment(), 'day'),
          appointments: allEvents.filter(evt => {
            const eventDate = evt.type === 'google_event' ? evt.start : evt.scheduled_date;
            return moment(eventDate).isSame(current, 'day');
          })
        });
        current.add(1, 'day');
      }

      return days;
    };

    const navigateMonth = (direction) => {
      const newDate = moment(currentDate).add(direction, 'month').toDate();
      setCurrentDate(newDate);
    };

    const navigateWeek = (direction) => {
      const newDate = moment(currentDate).add(direction, 'week').toDate();
      setCurrentDate(newDate);
    };

    const navigateDay = (direction) => {
      const newDate = moment(currentDate).add(direction, 'day').toDate();
      setCurrentDate(newDate);
    };

    const goToToday = () => {
      setCurrentDate(new Date());
    };

    const renderMonthView = () => {
      const days = getCalendarData();
      const weeks = [];
      
      for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
      }

      return (
        <div className="space-y-2 h-full flex flex-col">
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0 flex-1">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-3 text-center text-sm font-semibold text-gray-600 border-b">
                {day}
              </div>
            ))}
            
            {/* Calendar Days */}
            {days.map((day, index) => (
              <div
                key={index}
                className={`min-h-[120px] p-2 border border-gray-200 cursor-pointer hover:bg-gray-50 ${
                  !day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                } ${
                  day.isToday ? 'bg-blue-50 border-blue-300' : ''
                }`}
                onClick={() => onDateClick(day.date.format('YYYY-MM-DD'))}
              >
                <div className={`text-sm font-medium mb-1 ${
                  day.isToday ? 'text-blue-400' : ''
                }`}>
                  {day.date.format('D')}
                </div>
                
                {/* Appointments, Google Calendar Events, Blocks, and Pending Submissions */}
                <div className="space-y-1">
                  {(day.appointments || []).map((evt, idx) => {
                    // Handle color - can be object {bg, text, hover} or string hex color
                    let bgStyle = {};
                    let colorClasses = 'bg-blue-100 text-blue-800 hover:bg-blue-200';
                    const isPending = evt.type === 'pending';
                    const isBlock = evt.type === 'block';
                    
                    if (isPending) {
                      // Use photographer's color with prominent dashed border for pending
                      if (evt.color && typeof evt.color === 'object' && evt.color.bg) {
                        // Get the hex color from the dot class
                        const dotToHexMap = {
                          'bg-blue-500': '#3b82f6',
                          'bg-purple-500': '#a855f7',
                          'bg-green-500': '#22c55e',
                          'bg-orange-500': '#f97316',
                          'bg-pink-500': '#ec4899',
                          'bg-cyan-500': '#06b6d4',
                          'bg-yellow-500': '#eab308',
                          'bg-red-500': '#ef4444',
                          'bg-gray-500': '#6b7280'
                        };
                        const hexColor = dotToHexMap[evt.color.dot] || '#6b7280';
                        bgStyle = { 
                          backgroundColor: `${hexColor}30`,
                          border: `2px dashed ${hexColor}`,
                          boxShadow: `0 0 0 1px white, 0 0 0 3px ${hexColor}60`,
                          color: hexColor
                        };
                        colorClasses = 'hover:opacity-80 font-semibold';
                      } else {
                        bgStyle = { 
                          backgroundColor: '#f59e0b30',
                          border: '2px dashed #f59e0b',
                          boxShadow: '0 0 0 1px white, 0 0 0 3px #f59e0b60',
                          color: '#92400e'
                        };
                        colorClasses = 'hover:opacity-80 font-semibold';
                      }
                    } else if (evt.color) {
                      if (typeof evt.color === 'object' && evt.color.bg) {
                        colorClasses = `${evt.color.bg} ${evt.color.text} ${evt.color.hover}`;
                      } else if (typeof evt.color === 'string') {
                        bgStyle = { backgroundColor: evt.color, color: 'white' };
                        colorClasses = 'hover:opacity-80';
                      }
                    }
                    
                    return (
                      <div
                        key={`${evt.type}-${evt.id}-${idx}`}
                        className={`text-xs p-1 rounded cursor-pointer ${colorClasses} ${isBlock ? 'opacity-80' : ''}`}
                        style={bgStyle}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPending) {
                            setSelectedPendingSubmission(evt);
                            setShowPendingDetails(true);
                          } else if (evt.type === 'appointment') {
                            onAppointmentClick(evt);
                          } else if (evt.type === 'google_event') {
                            setSelectedEvent(evt);
                            setShowEventDetails(true);
                          } else if (evt.type === 'block') {
                            setSelectedBlock(evt);
                            setShowBlockDetails(true);
                          }
                        }}
                        title={
                          isPending
                            ? `⏳ PENDING: ${evt.client_name} - ${evt.scheduled_time}`
                            : evt.type === 'block' 
                              ? `🚫 ${evt.title || 'Blocked'}${evt.photographer_name ? ` - ${evt.photographer_name}` : ''}` 
                              : evt.type === 'google_event' 
                                ? `${evt.title} (${evt.team_member_name})` 
                                : `${evt.client_name} (${evt.photographer_name || 'Unassigned'})`
                        }
                      >
                        {isPending ? (
                          <>
                            <div className="font-medium truncate">⏳ {evt.client_name}</div>
                            <div className="truncate text-xs">{evt.scheduled_time} (Pending)</div>
                          </>
                        ) : evt.type === 'block' ? (
                          <>
                            <div className="font-medium truncate">🚫 {evt.title || 'Blocked'}{evt.photographer_name ? ` - ${evt.photographer_name}` : ''}</div>
                            <div className="truncate">{evt.all_day ? 'All Day' : evt.start_time}</div>
                          </>
                        ) : evt.type === 'google_event' ? (
                          <>
                            <div className="font-medium truncate">{evt.title}</div>
                            <div className="truncate text-xs">{moment(evt.start).format('h:mm A')}</div>
                          </>
                        ) : (
                          <>
                            <div className="font-medium truncate">{evt.client_name}</div>
                            <div className="truncate">{evt.scheduled_time}</div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderWeekView = () => {
      const startOfWeek = moment(currentDate).startOf('week');
      const days = [];
      const hours = [];
      const userTimezone = accountSettings.timezone || 'America/New_York';
      
      for (let h = 6; h <= 21; h++) {
        hours.push(h);
      }
      
      for (let i = 0; i < 7; i++) {
        const day = moment(startOfWeek).add(i, 'days');
        days.push({
          date: day,
          isToday: day.isSame(moment(), 'day'),
          appointments: allEvents.filter(evt => {
            const eventDate = evt.type === 'google_event' ? evt.start : (evt.scheduled_date || evt.start_date);
            return moment(eventDate).isSame(day, 'day');
          })
        });
      }

      const getEventPosition = (evt) => {
        let startTime, duration;
        if (evt.type === 'google_event') {
          startTime = moment(evt.start).tz(userTimezone);
          const endTime = moment(evt.end).tz(userTimezone);
          duration = endTime.diff(startTime, 'minutes');
        } else if (evt.type === 'block') {
          // Handle schedule blocks
          const [sh, sm] = (evt.start_time || '09:00').split(':').map(Number);
          const [eh, em] = (evt.end_time || '17:00').split(':').map(Number);
          startTime = moment(evt.start_date).tz(userTimezone).hour(sh).minute(sm);
          if (evt.all_day) {
            duration = (21 - 6) * 60; // Full visible day
            startTime = moment(evt.start_date).tz(userTimezone).hour(6).minute(0);
          } else {
            duration = (eh * 60 + em) - (sh * 60 + sm);
          }
        } else {
          let hour, minutes;
          const time = evt.scheduled_time || '';
          const ampmMatch = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
          const h24Match = time.match(/^(\d{1,2}):(\d{2})$/);
          if (ampmMatch) {
            hour = parseInt(ampmMatch[1]);
            minutes = parseInt(ampmMatch[2]);
            const period = ampmMatch[3].toUpperCase();
            if (period === 'PM' && hour !== 12) hour += 12;
            if (period === 'AM' && hour === 12) hour = 0;
          } else if (h24Match) {
            hour = parseInt(h24Match[1]);
            minutes = parseInt(h24Match[2]);
          } else {
            hour = 12;
            minutes = 0;
          }
          startTime = moment(evt.scheduled_date).tz(userTimezone).hour(hour).minute(minutes);
          duration = parseInt(evt.duration_minutes) || 60;
        }
        const startHour = startTime.hour() + startTime.minute() / 60;
        const top = (startHour - 6) * 48;
        const height = (duration / 60) * 48;
        return { top: Math.max(0, top), height: Math.max(24, height) };
      };

      const formatEventTime = (evt) => {
        if (evt.type === 'google_event') {
          return moment(evt.start).tz(userTimezone).format('h:mm A');
        }
        if (evt.type === 'block') {
          if (evt.all_day) return 'All Day';
          return moment(evt.start_time, 'HH:mm').format('h:mm A');
        }
        const time = evt.scheduled_time || '';
        if (/AM|PM/i.test(time)) return time;
        const match = time.match(/^(\d{1,2}):(\d{2})$/);
        if (match) return moment(time, 'HH:mm').format('h:mm A');
        return time;
      };

      return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-200">
            <div className="bg-gray-50 p-2 border-r border-gray-200"></div>
            {days.map((day, index) => (
              <div key={index} className={`p-2 text-center font-semibold border-r last:border-r-0 ${day.isToday ? 'bg-blue-50' : 'bg-gray-50'}`}>
                <div className="text-xs text-gray-500">{day.date.format('ddd')}</div>
                <div className={`text-lg ${day.isToday ? 'text-blue-600' : ''}`}>{day.date.format('D')}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[60px_repeat(7,1fr)] relative" style={{ height: `${hours.length * 48}px` }}>
            <div className="border-r border-gray-200 bg-gray-50">
              {hours.map((hour) => (
                <div key={hour} className="h-12 border-b border-gray-100 pr-2 text-right">
                  <span className="text-xs text-gray-500 relative -top-2">{moment().hour(hour).format('h A')}</span>
                </div>
              ))}
            </div>
            {days.map((day, dayIndex) => (
              <div key={dayIndex} className="relative border-r last:border-r-0 border-gray-200">
                {hours.map((hour) => (<div key={hour} className="h-12 border-b border-gray-100"></div>))}
                {(day.appointments || []).map((evt, idx) => {
                  const pos = getEventPosition(evt);
                  const isBlock = evt.type === 'block';
                  const isGoogle = evt.type === 'google_event';
                  const isPending = evt.type === 'pending';
                  
                  // Map Tailwind dot classes to hex colors
                  const dotToHex = {
                    'bg-blue-500': '#3b82f6',
                    'bg-purple-500': '#a855f7',
                    'bg-green-500': '#22c55e',
                    'bg-orange-500': '#f97316',
                    'bg-pink-500': '#ec4899',
                    'bg-cyan-500': '#06b6d4',
                    'bg-yellow-500': '#eab308',
                    'bg-amber-500': '#f59e0b',
                    'bg-red-500': '#ef4444',
                    'bg-gray-500': '#6b7280'
                  };
                  
                  // Determine background color - use photographer color for all events including pending
                  let bgColor;
                  if (evt.color && typeof evt.color === 'object' && evt.color.dot) {
                    bgColor = dotToHex[evt.color.dot] || '#3b82f6';
                  } else if (evt.color && typeof evt.color === 'string') {
                    bgColor = evt.color;
                  } else {
                    // Fallback colors
                    bgColor = isBlock ? '#6b7280' : isGoogle ? '#22c55e' : '#3b82f6';
                  }
                  
                  // Thinner dashed border style for pending events
                  const pendingBorderStyle = isPending ? `2px dashed ${bgColor}` : 'none';
                  const pendingBoxShadow = isPending ? `0 0 0 1px white, 0 0 0 3px ${bgColor}60` : 'none';
                  
                  return (
                    <div 
                      key={`${evt.type}-${evt.id}-${idx}`} 
                      className={`absolute left-1 right-1 rounded px-1 py-0.5 overflow-hidden cursor-pointer text-xs hover:opacity-90 ${isPending ? 'font-semibold' : 'text-white'}`}
                      style={{ 
                        top: `${pos.top}px`, 
                        height: `${pos.height}px`, 
                        backgroundColor: isPending ? `${bgColor}30` : bgColor,
                        opacity: isBlock ? 0.85 : 1,
                        border: pendingBorderStyle,
                        boxShadow: pendingBoxShadow,
                        color: isPending ? bgColor : 'white'
                      }} 
                      onClick={() => { 
                        if (isBlock) { 
                          setSelectedBlock(evt); 
                          setShowBlockDetails(true); 
                        } else if (isPending) {
                          // Show pending submission details modal
                          setSelectedPendingSubmission(evt);
                          setShowPendingDetails(true);
                        } else if (evt.type === 'appointment') { 
                          onAppointmentClick(evt); 
                        } else { 
                          setSelectedEvent(evt); 
                          setShowEventDetails(true); 
                        } 
                      }}
                    >
                      <div className="font-medium truncate">{isBlock ? '🚫 ' : isPending ? '⏳ ' : ''}{formatEventTime(evt)}</div>
                      <div className="truncate">{isPending ? `PENDING: ${evt.client_name}` : isBlock ? `${evt.title}${evt.photographer_name ? ` - ${evt.photographer_name}` : ''}` : isGoogle ? evt.title : evt.client_name}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderDayView = () => {
      const userTimezone = accountSettings.timezone || 'America/New_York';
      
      const dayEvents = allEvents.filter(evt => {
        const eventDate = evt.type === 'google_event' ? evt.start : evt.scheduled_date;
        return moment(eventDate).isSame(currentDate, 'day');
      });
      
      const hours = [];
      for (let h = 6; h <= 21; h++) { hours.push(h); }
      
      // Get photographers/team members for columns
      const activePhotographers = teamMembers.filter(m => 
        (m.role === 'Photographer' || m.role === 'Videographer') && m.is_active !== false
      );
      
      // Map Tailwind dot classes to hex colors
      const dotToHex = {
        'bg-blue-500': '#3b82f6', 'bg-purple-500': '#a855f7', 'bg-green-500': '#22c55e',
        'bg-orange-500': '#f97316', 'bg-pink-500': '#ec4899', 'bg-cyan-500': '#06b6d4',
        'bg-yellow-500': '#eab308', 'bg-red-500': '#ef4444', 'bg-gray-500': '#6b7280'
      };
      
      const getEventPosition = (evt) => {
        let startTime, duration;
        if (evt.type === 'google_event') {
          startTime = moment(evt.start).tz(userTimezone);
          const endTime = moment(evt.end).tz(userTimezone);
          duration = endTime.diff(startTime, 'minutes');
        } else if (evt.type === 'block') {
          const [sh, sm] = (evt.start_time || '09:00').split(':').map(Number);
          const [eh, em] = (evt.end_time || '17:00').split(':').map(Number);
          startTime = moment(evt.start_date).tz(userTimezone).hour(sh).minute(sm);
          if (evt.all_day) { duration = (21 - 6) * 60; startTime = moment(evt.start_date).tz(userTimezone).hour(6).minute(0); }
          else { duration = (eh * 60 + em) - (sh * 60 + sm); }
        } else {
          let hour, minutes;
          const time = evt.scheduled_time || '';
          const ampmMatch = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
          const h24Match = time.match(/^(\d{1,2}):(\d{2})$/);
          if (ampmMatch) { hour = parseInt(ampmMatch[1]); minutes = parseInt(ampmMatch[2]); const period = ampmMatch[3].toUpperCase(); if (period === 'PM' && hour !== 12) hour += 12; if (period === 'AM' && hour === 12) hour = 0; }
          else if (h24Match) { hour = parseInt(h24Match[1]); minutes = parseInt(h24Match[2]); }
          else { hour = 12; minutes = 0; }
          startTime = moment(evt.scheduled_date).tz(userTimezone).hour(hour).minute(minutes);
          duration = parseInt(evt.duration_minutes) || 60;
        }
        const startHour = startTime.hour() + startTime.minute() / 60;
        return { top: Math.max(0, (startHour - 6) * 60), height: Math.max(25, (duration / 60) * 60) };
      };
      
      const formatEventTime = (evt) => {
        if (evt.type === 'google_event') return moment(evt.start).tz(userTimezone).format('h:mm A');
        if (evt.type === 'block') return evt.all_day ? 'All Day' : moment(evt.start_time, 'HH:mm').format('h:mm A');
        const time = evt.scheduled_time || '';
        if (/AM|PM/i.test(time)) return time;
        const match = time.match(/^(\d{1,2}):(\d{2})$/);
        if (match) return moment(time, 'HH:mm').format('h:mm A');
        return time;
      };
      
      const isToday = moment(currentDate).isSame(moment(), 'day');
      
      // Group events by photographer
      const getPhotographerEvents = (photographerId) => {
        return dayEvents.filter(evt => {
          if (evt.type === 'block') return evt.photographer_id === photographerId;
          if (evt.type === 'google_event') return evt.team_member_id === photographerId;
          return evt.photographer_id === photographerId;
        });
      };
      
      const unassignedEvents = dayEvents.filter(evt => {
        if (evt.type === 'pending') return !evt.photographer_id || evt.photographer_id === '';
        return !evt.photographer_id && !evt.team_member_id;
      });
      
      const columns = activePhotographers.length > 0 
        ? [...activePhotographers.filter(m => {
            if (Object.keys(photographerFilters || {}).length === 0) return true;
            return photographerFilters[m.id] !== false;
          }), { id: 'unassigned', name: 'Unassigned', color: '#6b7280' }]
        : [{ id: 'all', name: 'All Events', color: '#3b82f6' }];

      return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Loading indicator for drive times */}
          {loadingDriveTimes && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-sm text-amber-700">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Calculating drive times...
            </div>
          )}
          
          {/* Drive times legend */}
          {!loadingDriveTimes && Object.keys(dayDriveTimes).length > 0 && (
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-4 text-xs">
              <span className="text-gray-500 font-medium">Legend:</span>
              <div className="flex items-center gap-1">
                <div className="w-4 h-3 bg-amber-100 border border-amber-300 border-dashed rounded-sm"></div>
                <span className="text-gray-600">🚗 Drive time</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-3 border border-gray-300 rounded-sm" style={{
                  background: `repeating-linear-gradient(-45deg, #f3f4f6, #f3f4f6 2px, #e5e7eb 2px, #e5e7eb 4px)`
                }}></div>
                <span className="text-gray-600">⏸ Gap/Free time</span>
              </div>
              {/* Info when no drive segments exist */}
              {Object.values(dayDriveTimes).every(m => m.drive_segments?.length === 0) && (
                <span className="text-amber-600 ml-auto text-[10px]">
                  ℹ️ Drive times show when photographers have 2+ appointments or home addresses set in Team settings
                </span>
              )}
            </div>
          )}
          
          {/* Header with photographer columns */}
          <div className={`grid border-b border-gray-200`} style={{ gridTemplateColumns: `80px repeat(${columns.length}, 1fr)` }}>
            <div className="bg-gray-50 p-2 border-r border-gray-200 flex items-center justify-center">
              <span className="text-xs text-gray-500 font-medium">{moment(currentDate).format('MMM D')}</span>
            </div>
            {columns.map((col, idx) => {
              // Build Google Maps directions URL for this photographer's appointments today
              const photogAppts = col.id !== 'unassigned' && col.id !== 'all' 
                ? dayEvents
                    .filter(evt => evt.type !== 'block' && evt.type !== 'google_event' && evt.photographer_id === col.id && evt.property_address)
                    .sort((a, b) => {
                      const timeA = a.scheduled_time || '00:00';
                      const timeB = b.scheduled_time || '00:00';
                      return timeA.localeCompare(timeB);
                    })
                : [];
              const homeAddress = col.home_base_address || '';
              const mapsUrl = photogAppts.length > 0 ? (() => {
                const stops = photogAppts.map(a => encodeURIComponent(a.property_address));
                const origin = homeAddress ? encodeURIComponent(homeAddress) : stops[0];
                const destination = homeAddress ? encodeURIComponent(homeAddress) : stops[stops.length - 1];
                const waypoints = homeAddress
                  ? stops.join('|')
                  : stops.slice(1, -1).join('|');
                return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? '&waypoints=' + waypoints : ''}`;
              })() : null;

              return (
                <div key={col.id} className={`p-2 text-center border-r border-gray-200 ${isToday ? 'bg-blue-50' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: col.color || dotToHex[col.dot_color] || '#3b82f6' }}></div>
                    <span className="text-sm font-medium text-gray-700 truncate">{col.name}</span>
                    {mapsUrl && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(mapsUrl, '_blank', 'noopener,noreferrer'); }}
                        title={`Driving route for ${col.name} (${photogAppts.length} stops)`}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        data-testid={`maps-link-${col.id}`}
                      >
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                        Route
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Time Grid with columns */}
          <div className={`grid relative`} style={{ gridTemplateColumns: `80px repeat(${columns.length}, 1fr)`, height: `${hours.length * 50}px` }}>
            {/* Time Labels */}
            <div className="border-r border-gray-200 bg-gray-50">
              {hours.map((hour) => (
                <div key={hour} className="h-[50px] border-b border-gray-100 pr-2 text-right flex items-start justify-end pt-1">
                  <span className="text-xs text-gray-500">{moment().hour(hour).format('h A')}</span>
                </div>
              ))}
            </div>
            
            {/* Photographer Columns */}
            {columns.map((col, colIdx) => {
              const colEvents = col.id === 'all' ? dayEvents : col.id === 'unassigned' ? unassignedEvents : getPhotographerEvents(col.id);
              const memberDriveTimes = col.id !== 'all' && col.id !== 'unassigned' ? dayDriveTimes[col.id] : null;
              const driveSegments = memberDriveTimes?.drive_segments || [];
              
              // Helper function to calculate position for drive segments
              const getDrivePosition = (segment) => {
                const [startHour, startMin] = segment.start_time.split(':').map(Number);
                const [endHour, endMin] = segment.end_time.split(':').map(Number);
                const startDecimal = startHour + startMin / 60;
                const endDecimal = endHour + endMin / 60;
                const top = Math.max(0, (startDecimal - 6) * 50);
                const height = Math.max(15, (endDecimal - startDecimal) * 50);
                return { top, height };
              };
              
              return (
                <div key={col.id} className="relative border-r border-gray-200">
                  {hours.map((hour) => (
                    <div key={hour} className="h-[50px] border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                      onClick={() => onDateClick(moment(currentDate).format('YYYY-MM-DD'))} />
                  ))}
                  
                  {/* Drive time segments */}
                  {driveSegments.map((segment, idx) => {
                    const pos = getDrivePosition(segment);
                    const isDrive = segment.type === 'drive' || segment.type === 'drive_home';
                    const isGap = segment.type === 'gap';
                    
                    return (
                      <div 
                        key={`drive-${idx}`}
                        className={`absolute left-1 right-1 rounded-sm px-1 py-0.5 text-[9px] pointer-events-none z-5 ${
                          isDrive 
                            ? 'bg-amber-100 border border-amber-300 border-dashed' 
                            : 'border border-gray-300'
                        }`}
                        style={{ 
                          top: `${pos.top}px`, 
                          height: `${pos.height}px`,
                          opacity: 0.9,
                          ...(isGap && {
                            background: `repeating-linear-gradient(
                              -45deg,
                              #f3f4f6,
                              #f3f4f6 4px,
                              #e5e7eb 4px,
                              #e5e7eb 8px
                            )`
                          })
                        }}
                        title={isDrive 
                          ? `Drive: ${segment.from_location} → ${segment.to_location} (${segment.duration_minutes >= 60 ? `${Math.floor(segment.duration_minutes / 60)}h ${segment.duration_minutes % 60}m` : `${segment.duration_minutes} min`})`
                          : `Gap: ${segment.duration_minutes >= 60 ? `${Math.floor(segment.duration_minutes / 60)}h ${segment.duration_minutes % 60}m` : `${segment.duration_minutes} min`}`
                        }
                      >
                        <div className={`truncate ${isDrive ? 'text-amber-700' : 'text-gray-500'}`}>
                          {isDrive ? (
                            <>
                              🚗 {segment.duration_minutes >= 60 
                                ? `${Math.floor(segment.duration_minutes / 60)}h${segment.duration_minutes % 60 > 0 ? ` ${segment.duration_minutes % 60}m` : ''}`
                                : `${segment.duration_minutes}m`}
                              {segment.type === 'drive_home' && ' → Home'}
                            </>
                          ) : (
                            <>⏸ {segment.duration_minutes >= 60 
                              ? `${Math.floor(segment.duration_minutes / 60)}h${segment.duration_minutes % 60 > 0 ? ` ${segment.duration_minutes % 60}m` : ''}`
                              : `${segment.duration_minutes}m`} gap</>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Appointments */}
                  {(colEvents || []).map((evt, idx) => {
                    const pos = getEventPosition(evt);
                    const isBlock = evt.type === 'block';
                    const isGoogle = evt.type === 'google_event';
                    const isPending = evt.type === 'pending';
                    let bgColor = col.color || '#3b82f6';
                    if (evt.color && typeof evt.color === 'object' && evt.color.dot) bgColor = dotToHex[evt.color.dot] || bgColor;
                    else if (evt.color && typeof evt.color === 'string') bgColor = evt.color;
                    
                    return (
                      <div key={`${evt.type}-${evt.id}-${idx}`}
                        className={`absolute left-1 right-1 rounded px-2 py-1 overflow-hidden cursor-pointer text-xs hover:opacity-90 z-10 ${isPending ? 'font-semibold' : 'text-white'}`}
                        style={{ 
                          top: `${pos.top * 50/60}px`, height: `${Math.max(22, pos.height * 50/60)}px`,
                          backgroundColor: isPending ? `${bgColor}30` : bgColor,
                          border: isPending ? `2px dashed ${bgColor}` : 'none',
                          color: isPending ? bgColor : 'white'
                        }}
                        onClick={() => { 
                          if (isBlock) { setSelectedBlock(evt); setShowBlockDetails(true); }
                          else if (isPending) { setSelectedPendingSubmission(evt); setShowPendingDetails(true); }
                          else if (evt.type === 'appointment') { onAppointmentClick(evt); }
                          else { setSelectedEvent(evt); setShowEventDetails(true); }
                        }}>
                        <div className="truncate font-medium">{isPending ? `⏳ ${evt.client_name}` : isBlock ? '🚫 Blocked' : evt.client_name || evt.title}</div>
                        <div className="truncate text-[10px] opacity-80">{formatEventTime(evt)}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            
            {/* Current time indicator */}
            {isToday && (() => {
              const now = moment().tz(userTimezone);
              const currentHour = now.hour() + now.minute() / 60;
              if (currentHour >= 6 && currentHour <= 21) {
                return <div className="absolute left-[80px] right-0 border-t-2 border-red-500 z-10 pointer-events-none" style={{ top: `${(currentHour - 6) * 50}px` }}>
                  <div className="absolute -left-1 -top-1.5 w-3 h-3 bg-red-500 rounded-full"></div>
                </div>;
              }
              return null;
            })()}
          </div>
        </div>
      );
    };

    const renderAgendaView = () => {
      const monthStart = moment(currentDate).startOf('month');
      const monthEnd = moment(currentDate).endOf('month');
      
      const monthEvents = allEvents.filter(evt => {
        const eventDate = evt.type === 'google_event' ? evt.start : evt.scheduled_date;
        const evtDate = moment(eventDate);
        return evtDate.isBetween(monthStart, monthEnd, 'day', '[]');
      }).sort((a, b) => {
        const dateA = a.type === 'google_event' ? moment(a.start).format('YYYY-MM-DD HH:mm') : `${a.scheduled_date} ${a.scheduled_time}`;
        const dateB = b.type === 'google_event' ? moment(b.start).format('YYYY-MM-DD HH:mm') : `${b.scheduled_date} ${b.scheduled_time}`;
        return dateA.localeCompare(dateB);
      });

      return (
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded">
            <h3 className="text-lg font-semibold">{moment(currentDate).format('MMMM YYYY')} Events</h3>
          </div>
          
          <div className="space-y-3">
            {monthEvents.length > 0 ? (
              monthEvents.map((evt, idx) => {
                const isBlock = evt.type === 'block';
                const isGoogle = evt.type === 'google_event';
                
                // Map Tailwind dot classes to hex colors
                const dotToHex = {
                  'bg-blue-500': '#3b82f6',
                  'bg-purple-500': '#a855f7',
                  'bg-green-500': '#22c55e',
                  'bg-orange-500': '#f97316',
                  'bg-pink-500': '#ec4899',
                  'bg-cyan-500': '#06b6d4',
                  'bg-yellow-500': '#eab308',
                  'bg-red-500': '#ef4444',
                  'bg-gray-500': '#6b7280'
                };
                
                // Get color for left border
                let borderColor;
                if (evt.color && typeof evt.color === 'object' && evt.color.dot) {
                  borderColor = dotToHex[evt.color.dot] || '#3b82f6';
                } else if (evt.color && typeof evt.color === 'string') {
                  borderColor = evt.color;
                } else {
                  borderColor = isBlock ? '#6b7280' : isGoogle ? '#22c55e' : '#3b82f6';
                }
                
                return (
                  <div
                    key={`${evt.type}-${evt.id}-${idx}`}
                    className="border rounded p-4 cursor-pointer hover:bg-gray-50"
                    style={{ borderLeftWidth: '4px', borderLeftColor: borderColor }}
                    onClick={() => {
                      if (isBlock) {
                        setSelectedBlock(evt);
                        setShowBlockDetails(true);
                      } else if (evt.type === 'appointment') {
                        onAppointmentClick(evt);
                      } else {
                        setSelectedEvent(evt);
                        setShowEventDetails(true);
                      }
                    }}
                  >
                    {isBlock ? (
                      <div className="flex items-start gap-6">
                        <div className="flex-1">
                          <div className="font-semibold text-base mb-1">🚫 {evt.title || 'Blocked'}{evt.photographer_name ? ` - ${evt.photographer_name}` : ''}</div>
                          <div className="text-sm text-gray-600">{evt.reason || 'Schedule blocked'}</div>
                        </div>
                        <div className="text-right min-w-[160px] flex-shrink-0">
                          <div className="font-semibold text-gray-800 text-sm mb-1">{moment(evt.start_date).format('MMM D, YYYY')}</div>
                          <div className="font-medium text-gray-600">{evt.all_day ? 'All Day' : evt.start_time}</div>
                        </div>
                      </div>
                    ) : isGoogle ? (
                      <div className="flex items-start gap-6">
                        <div className="flex-1">
                          <div className="font-semibold text-green-700 text-base mb-1">{evt.title}</div>
                          <div className="text-sm text-gray-600">{evt.team_member_name}</div>
                        </div>
                        <div className="text-right min-w-[160px] flex-shrink-0">
                          <div className="font-semibold text-gray-800 text-sm mb-1">{moment(evt.start).format('MMM D, YYYY')}</div>
                          <div className="font-medium text-green-600">{moment(evt.start).format('h:mm A')}</div>
                          <div className="text-xs text-gray-500">to {moment(evt.end).format('h:mm A')}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-6">
                        <div className="flex-1">
                          <div className="font-semibold text-base mb-1">{evt.client_name}</div>
                          <div className="text-sm text-gray-600 mb-1">{evt.service_type}</div>
                          {evt.selected_products && evt.selected_products.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {evt.selected_products.map((productId) => {
                                const product = products.find(p => p.id === productId);
                                return product ? (
                                  <span key={productId} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-300">
                                    {product.name}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                        <div className="text-right min-w-[160px] flex-shrink-0">
                          <div className="font-semibold text-gray-800 text-sm mb-1">{moment(evt.scheduled_date).format('MMM D, YYYY')}</div>
                          <div className="font-medium text-blue-400">{evt.scheduled_time}</div>
                          <div className="text-xs text-gray-500">{evt.photographer_name || 'Unassigned'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No events scheduled for {moment(currentDate).format('MMMM YYYY')}</p>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        {/* Calendar Navigation */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
            >
              Today
            </button>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  if (calendarView === 'month') navigateMonth(-1);
                  else if (calendarView === 'day') navigateDay(-1);
                  else navigateWeek(-1);
                }}
                className="w-10 h-10 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center justify-center text-lg font-bold"
              >
                ←
              </button>
              
              <div className="relative" ref={datePickerRef}>
                <div 
                  className="px-4 py-2 bg-gray-100 rounded min-w-[200px] text-center border border-gray-300 cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  title="Click to pick a date"
                >
                  <span className="font-semibold text-gray-900 flex items-center justify-center gap-2">
                    {calendarView === 'month' && moment(currentDate).format('MMMM YYYY')}
                    {calendarView === 'week' && `Week of ${moment(currentDate).startOf('week').format('MMM D, YYYY')}`}
                    {calendarView === 'day' && moment(currentDate).format('dddd, MMMM D, YYYY')}
                    {calendarView === 'agenda' && `${moment(currentDate).format('MMMM YYYY')} Agenda`}
                    <svg className={`w-4 h-4 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </div>
                
                {/* Date Picker Popover */}
                {showDatePicker && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-2">
                    <DayPicker
                      mode="single"
                      selected={new Date(currentDate)}
                      onSelect={(date) => {
                        if (date) {
                          setCurrentDate(date);
                          setShowDatePicker(false);
                        }
                      }}
                      defaultMonth={new Date(currentDate)}
                      showOutsideDays
                      classNames={{
                        months: "flex flex-col",
                        month: "space-y-2",
                        caption: "flex justify-center pt-1 relative items-center mb-2",
                        caption_label: "text-sm font-semibold text-gray-900",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 rounded hover:bg-gray-100 flex items-center justify-center",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse",
                        head_row: "flex",
                        head_cell: "text-gray-500 rounded-md w-9 font-normal text-[0.75rem] text-center",
                        row: "flex w-full mt-1",
                        cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
                        day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-blue-50 flex items-center justify-center cursor-pointer",
                        day_selected: "bg-blue-600 text-white hover:bg-blue-700",
                        day_today: "bg-amber-100 text-amber-900 font-semibold",
                        day_outside: "text-gray-300",
                        day_disabled: "text-gray-300 opacity-50",
                      }}
                      components={{
                        IconLeft: () => <span className="text-lg">←</span>,
                        IconRight: () => <span className="text-lg">→</span>,
                      }}
                    />
                    <div className="border-t border-gray-200 pt-2 mt-2 flex justify-center">
                      <button
                        onClick={() => {
                          setCurrentDate(new Date());
                          setShowDatePicker(false);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Go to Today
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => {
                  if (calendarView === 'month') navigateMonth(1);
                  else if (calendarView === 'day') navigateDay(1);
                  else navigateWeek(1);
                }}
                className="w-10 h-10 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center justify-center text-lg font-bold"
              >
                →
              </button>
            </div>
          </div>
          
          <div className="flex space-x-2">
            {['month', 'week', 'day', 'agenda'].map(viewType => (
              <button
                key={viewType}
                onClick={() => {
                  console.log(`Switching to ${viewType} calendarView`);
                  setCalendarView(viewType);
                }}
                className={`px-4 py-2 rounded capitalize font-medium ${
                  calendarView === viewType 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                {viewType}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar Content */}
        <div>
          {calendarView === 'month' && renderMonthView()}
          {calendarView === 'week' && renderWeekView()}
          {calendarView === 'day' && renderDayView()}
          {calendarView === 'agenda' && renderAgendaView()}
        </div>
      </div>
    );
  };

export default SimpleCalendar;
